// ============================================
// UBUS V2.1 GPS 位置上报管理器
// 浏览器 Geolocation API + Supabase location_logs
// Supports trip GPS + shift GPS (trip_id null) per CONF-20260716
// ============================================

import { supabase, toGeoPointWKT, fetchSystemSettings } from '@ubus/shared';

interface CachedReport {
  trip_id: string | null;
  driver_id: string;
  vehicle_id?: string | null;
  geopoint: string;
  accuracy?: number;
  speed?: number;
  timestamp: string;
}

const CACHE_KEY = 'ubus-gps-failed-reports';
const MAX_CACHE_SIZE = 100;
const STOP_AT_KEY_PREFIX = 'ubus-gps-stop-at-';
const ACTIVE_GPS_KEY = 'ubus-gps-active';
const SHIFT_STOP_KEY = 'ubus-gps-stop-at-shift';

/** Default post-arrival GPS window per plan (15 minutes). */
export const GPS_POST_ARRIVAL_DELAY_MS = 15 * 60 * 1000;

function getCurrentPositionWithFallback(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        console.warn('[GPSManager] High accuracy failed, retrying with fallback:', error.message);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('[GPSManager] Using geolocation fallback (enableHighAccuracy: false)');
            resolve(position);
          },
          reject,
          {
            enableHighAccuracy: false,
            timeout: 30000,
            maximumAge: 60000,
          }
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
      }
    );
  });
}

class GPSManager {
  private tripId: string | null;
  private driverId: string;
  private vehicleId: string | null;
  private intervalId: number | null;
  private stopTimeoutId: number | null;
  private isReporting: boolean;
  private lastPosition: { lat: number; lng: number } | null;
  private reportIntervalMs: number;
  private mode: 'trip' | 'shift';

  private static shiftInstance: GPSManager | null = null;

  constructor(tripId: string | null, driverId: string, vehicleId: string | null = null) {
    this.tripId = tripId;
    this.driverId = driverId;
    this.vehicleId = vehicleId;
    this.intervalId = null;
    this.stopTimeoutId = null;
    this.isReporting = false;
    this.lastPosition = null;
    this.reportIntervalMs = 120_000;
    this.mode = tripId ? 'trip' : 'shift';
  }

  /** Start shift-level GPS (no trip). Stops any previous shift instance. */
  static async startShiftReporting(driverId: string, vehicleId?: string | null): Promise<GPSManager | null> {
    if (localStorage.getItem('ubus_gps_enabled') === 'false') return null;
    GPSManager.stopShiftReporting();
    const manager = new GPSManager(null, driverId, vehicleId ?? null);
    const started = await manager.startReporting();
    if (!started) return null;
    GPSManager.shiftInstance = manager;
    localStorage.setItem(
      ACTIVE_GPS_KEY,
      JSON.stringify({ tripId: null, driverId, vehicleId: vehicleId ?? null, mode: 'shift' })
    );
    return manager;
  }

  static stopShiftReporting(): void {
    if (GPSManager.shiftInstance) {
      GPSManager.shiftInstance.stopReporting();
      GPSManager.shiftInstance = null;
    }
    try {
      const raw = localStorage.getItem(ACTIVE_GPS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { mode?: string };
        if (parsed.mode === 'shift') localStorage.removeItem(ACTIVE_GPS_KEY);
      }
    } catch {
      // ignore
    }
  }

  /** Whether this trip still warrants GPS reporting (server-driven tracking window). */
  async shouldContinueReporting(): Promise<boolean> {
    if (this.mode === 'shift' || !this.tripId) return true;

    const { data, error } = await supabase
      .from('trips')
      .select('status, tracking_active, source, assignment_status')
      .eq('id', this.tripId)
      .single();

    if (error || !data) return false;

    if (data.source !== 'platform' || data.assignment_status !== 'accepted') {
      return false;
    }

    if (data.status === 'in_progress') return true;
    if (data.status === 'dispatched' && data.tracking_active === true) return true;

    return false;
  }

  /** Start periodic GPS position reporting. Returns true if reporting actually started. */
  async startReporting(): Promise<boolean> {
    if (this.isReporting) return true;

    if (localStorage.getItem('ubus_gps_enabled') === 'false') {
      return false;
    }

    if (localStorage.getItem('ubus_offline_mode') === 'true') {
      return false;
    }

    if (!navigator.geolocation) {
      return false;
    }

    if (!this.hasScheduledStop()) {
      const shouldContinue = await this.shouldContinueReporting();
      if (!shouldContinue) return false;
    }

    const settings = await fetchSystemSettings();
    this.reportIntervalMs = Math.max(30, settings.gps_report_interval) * 1000;

    this.isReporting = true;

    await this.reportPosition();

    this.intervalId = window.setInterval(() => {
      this.reportPosition();
    }, this.reportIntervalMs);

    console.log(
      `[GPSManager] Started ${this.mode} reporting` +
        (this.tripId ? ` for trip ${this.tripId}` : '') +
        ` every ${this.reportIntervalMs / 1000}s`
    );

    await GPSManager.flushCachedReports();
    return true;
  }

  /** Stop GPS position reporting immediately and clear any scheduled stop. */
  stopReporting(): void {
    if (this.stopTimeoutId !== null) {
      clearTimeout(this.stopTimeoutId);
      this.stopTimeoutId = null;
    }
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isReporting = false;
    const stopKey = this.tripId ? `${STOP_AT_KEY_PREFIX}${this.tripId}` : SHIFT_STOP_KEY;
    localStorage.removeItem(stopKey);
    if (this.mode === 'trip' && this.tripId && this.getActiveTripId() === this.tripId) {
      localStorage.removeItem(ACTIVE_GPS_KEY);
    }
    if (this.mode === 'shift' && GPSManager.shiftInstance === this) {
      GPSManager.shiftInstance = null;
    }
    console.log(`[GPSManager] Stopped ${this.mode} reporting` + (this.tripId ? ` for trip ${this.tripId}` : ''));
  }

  /** Keep reporting for delayMs after arrival, then stop automatically. */
  scheduleStopAfter(delayMs: number): void {
    if (!this.tripId) return;

    if (this.stopTimeoutId !== null) {
      clearTimeout(this.stopTimeoutId);
    }

    const stopAt = Date.now() + delayMs;
    localStorage.setItem(`${STOP_AT_KEY_PREFIX}${this.tripId}`, String(stopAt));
    localStorage.setItem(ACTIVE_GPS_KEY, JSON.stringify({ tripId: this.tripId, driverId: this.driverId, mode: 'trip' }));

    this.stopTimeoutId = window.setTimeout(() => {
      this.finishScheduledStop();
    }, delayMs);

    console.log(`[GPSManager] Scheduled stop for trip ${this.tripId} in ${Math.round(delayMs / 1000)}s`);
  }

  /** Whether a future stop is scheduled (GPS should keep running after leaving the page). */
  hasScheduledStop(): boolean {
    if (!this.tripId) return false;
    const stopAt = localStorage.getItem(`${STOP_AT_KEY_PREFIX}${this.tripId}`);
    if (!stopAt) return false;
    return parseInt(stopAt, 10) > Date.now();
  }

  private finishScheduledStop(): void {
    this.stopTimeoutId = null;
    this.stopReporting();
  }

  private getActiveTripId(): string | null {
    try {
      const raw = localStorage.getItem(ACTIVE_GPS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { tripId?: string | null };
      return parsed.tripId ?? null;
    } catch {
      return null;
    }
  }

  /** Resume GPS reporting after page reload if post-arrival window is still active. */
  static async restoreIfPending(driverId: string): Promise<GPSManager | null> {
    const raw = localStorage.getItem(ACTIVE_GPS_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as {
        tripId?: string | null;
        driverId?: string;
        vehicleId?: string | null;
        mode?: string;
      };

      if (parsed.driverId !== driverId) {
        localStorage.removeItem(ACTIVE_GPS_KEY);
        return null;
      }

      if (parsed.mode === 'shift' || !parsed.tripId) {
        return GPSManager.startShiftReporting(driverId, parsed.vehicleId);
      }

      const tripId = parsed.tripId;
      const stopAtRaw = localStorage.getItem(`${STOP_AT_KEY_PREFIX}${tripId}`);
      if (stopAtRaw) {
        const stopAt = parseInt(stopAtRaw, 10);
        if (stopAt <= Date.now()) {
          localStorage.removeItem(ACTIVE_GPS_KEY);
          localStorage.removeItem(`${STOP_AT_KEY_PREFIX}${tripId}`);
          return null;
        }
      }

      const manager = new GPSManager(tripId, driverId);
      const started = await manager.startReporting();
      if (!started) {
        localStorage.removeItem(ACTIVE_GPS_KEY);
        return null;
      }

      if (stopAtRaw) {
        const remaining = parseInt(stopAtRaw, 10) - Date.now();
        if (remaining > 0) manager.scheduleStopAfter(remaining);
      }

      return manager;
    } catch {
      localStorage.removeItem(ACTIVE_GPS_KEY);
      return null;
    }
  }

  /** Flush cached GPS reports from localStorage to Supabase. */
  static async flushCachedReports(): Promise<void> {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return;
      const cached: CachedReport[] = JSON.parse(raw);
      if (cached.length === 0) return;

      console.log(`[GPSManager] Flushing ${cached.length} cached reports`);

      const batchSize = 10;
      let successCount = 0;

      for (let i = 0; i < cached.length; i += batchSize) {
        const batch = cached.slice(i, i + batchSize);
        const { error } = await supabase.from('location_logs').insert(batch);

        if (!error) {
          successCount += batch.length;
        } else {
          console.warn('[GPSManager] Flush batch error:', error.message);
          break;
        }
      }

      if (successCount > 0) {
        const remaining = cached.slice(successCount);
        localStorage.setItem(CACHE_KEY, JSON.stringify(remaining));
        console.log(`[GPSManager] Flushed ${successCount} cached reports, ${remaining.length} remaining`);
      }
    } catch (e) {
      console.warn('[GPSManager] Flush error:', e);
    }
  }

  getReportingStatus(): boolean {
    return this.isReporting;
  }

  getLastPosition(): { lat: number; lng: number } | null {
    return this.lastPosition;
  }

  private async reportPosition(): Promise<void> {
    if (!navigator.geolocation) return;

    if (!this.hasScheduledStop()) {
      const shouldContinue = await this.shouldContinueReporting();
      if (!shouldContinue) {
        this.stopReporting();
        return;
      }
    }

    try {
      const position = await getCurrentPositionWithFallback();
      const { latitude, longitude, accuracy, speed } = position.coords;
      this.lastPosition = { lat: latitude, lng: longitude };

      const geopoint = toGeoPointWKT(latitude, longitude);

      try {
        const { error } = await supabase.from('location_logs').insert({
          trip_id: this.tripId,
          driver_id: this.driverId,
          vehicle_id: this.vehicleId,
          geopoint,
          accuracy: accuracy ?? null,
          speed: speed ? speed * 3.6 : null,
          timestamp: new Date().toISOString(),
        });

        if (error) {
          console.error('[GPSManager] Insert error:', error.message);
          this.cacheFailedReport(geopoint, accuracy ?? undefined, speed ? speed * 3.6 : undefined);
        } else {
          console.log(`[GPSManager] Position reported: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        }
      } catch (err) {
        console.error('[GPSManager] Network error:', err);
        this.cacheFailedReport(geopoint, accuracy ?? undefined, speed ? speed * 3.6 : undefined);
      }
    } catch (error) {
      const message = error instanceof GeolocationPositionError ? error.message : String(error);
      console.warn('[GPSManager] Geolocation error:', message);
    }
  }

  private cacheFailedReport(geopoint: string, accuracy?: number, speed?: number): void {
    try {
      const cached: CachedReport = {
        trip_id: this.tripId,
        driver_id: this.driverId,
        vehicle_id: this.vehicleId,
        geopoint,
        accuracy,
        speed,
        timestamp: new Date().toISOString(),
      };

      const existing: CachedReport[] = JSON.parse(localStorage.getItem(CACHE_KEY) || '[]');

      if (existing.length >= MAX_CACHE_SIZE) {
        existing.splice(0, existing.length - MAX_CACHE_SIZE + 1);
      }

      existing.push(cached);
      localStorage.setItem(CACHE_KEY, JSON.stringify(existing));
    } catch (e) {
      console.warn('[GPSManager] Failed to cache report:', e);
    }
  }
}

export default GPSManager;
